import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { PixelArrow, PixelButton } from '@/components/generals/pixel-button'

import SkateDino from '@/components/pixel/skate-dino'
import PixelCloud from '@/components/pixel/pixel-cloud'
import PixelFlower from '@/components/pixel/pixel-flower'

import { HERO } from '@/constants/content'
import { ICON_ARROW } from '@/constants/sprites'

interface PopLettersProps {
    text: string
    startDelay?: number
    step?: number
    className?: string
}

/** Letters that pop in one after another, and hop when hovered. Hidden from screen readers. */
const PopLetters: FC<PopLettersProps> = ({
    text,
    startDelay = 0,
    step = 60,
    className
}): ReactNode => {
    return (
        <span aria-hidden='true' className={cn('block whitespace-nowrap', className)}>
            {Array.from(text).map((char, index) => {
                if (char === ' ') return <span key={index} className='inline-block w-[0.32em]' />

                return (
                    <span
                        key={index}
                        className='inline-block animate-letter-pop transition-[translate,color] duration-150 ease-[steps(2,end)] hover:-translate-y-[0.08em] hover:text-bubblegum'
                        style={{ animationDelay: `${startDelay + index * step}ms` }}
                    >
                        {char}
                    </span>
                )
            })}
        </span>
    )
}

const HeroSection: FC = (): ReactNode => {
    return (
        <section
            id='hero'
            aria-labelledby='hero-title'
            className='relative isolate flex min-h-[100svh] flex-col overflow-x-clip pt-24 pb-8 md:pt-28 lg:min-h-[max(100svh,760px)]'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <PixelCloud className='absolute top-[17%] left-[3%] w-[clamp(120px,17vw,260px)] animate-drift [--drift:56px]' />
                <PixelCloud shape='small' className='absolute top-[12%] right-[7%] w-[clamp(90px,11vw,170px)] animate-drift [--drift:-44px] [--drift-duration:17s]' />
                <PixelCloud className='absolute top-[40%] right-[10%] hidden w-[clamp(110px,14vw,220px)] animate-drift [--drift:-64px] [--drift-duration:28s] md:block' />
                <PixelCloud shape='small' className='absolute bottom-[30%] left-[15%] hidden w-[clamp(70px,8vw,130px)] animate-drift [--drift:40px] [--drift-duration:24s] sm:block' />
                <PixelFlower className='absolute bottom-[14%] -left-14 w-40 animate-spin-slow md:-left-10 md:w-52' />
                <PixelFlower centerColor='var(--bubblegum)' className='absolute top-[30%] right-[3%] hidden w-14 animate-sway md:block' />
            </div>

            <div className='mx-auto flex w-full max-w-7xl flex-1 flex-col items-center px-5 text-center md:px-8'>
                <p className='mt-4 inline-flex animate-pop-in items-center gap-2 border-2 border-ink bg-white px-3 py-1.5 font-heading text-sm shadow-pixel-sm md:text-base'>
                    <span aria-hidden='true' className='size-2 animate-caret bg-fern' />
                    {HERO.eyebrow}
                </p>

                <h1 id='hero-title' className='mt-6 font-heading leading-[0.82] tracking-tight text-ink md:mt-8'>
                    <span className='sr-only'>Marketing is our art</span>
                    <PopLetters text={HERO.lines[0]} startDelay={150} className='text-[clamp(3.25rem,13.5vw,11.5rem)]' />
                    <PopLetters text={HERO.lines[1]} startDelay={720} className='text-[clamp(2.4rem,9.6vw,8.25rem)]' />
                </h1>

                <p
                    className='mt-7 animate-pop-in text-base font-semibold tracking-[0.18em] uppercase md:mt-9 md:text-2xl'
                    style={{ animationDelay: '1300ms' }}
                >
                    {HERO.subtitle}
                </p>

                <div
                    className='mt-8 flex animate-pop-in flex-wrap items-center justify-center gap-x-7 gap-y-4'
                    style={{ animationDelay: '1450ms' }}
                >
                    <PixelButton href='#contacts' size='lg' arrow>
                        {HERO.cta}
                    </PixelButton>

                    <a
                        href='#services'
                        className='group/button inline-flex items-center gap-2 font-heading text-lg text-ink underline decoration-2 underline-offset-[6px] hover:decoration-bubblegum md:text-xl'
                    >
                        {HERO.secondary}
                        <PixelArrow className='rotate-90' />
                    </a>
                </div>

                <SkateDino
                    interactive
                    rollIn
                    className='mx-auto mt-16 w-[252px] [--roll-from:120vw] sm:w-[294px] lg:absolute lg:right-[4%] lg:bottom-[clamp(6.5rem,15vh,9rem)] lg:mx-0 lg:mt-0 xl:w-[336px] 2xl:right-[7%] 2xl:w-[378px]'
                />
            </div>

            <div className='mx-auto mt-14 grid w-full max-w-7xl gap-5 px-5 text-[11px] leading-snug font-bold tracking-wide uppercase sm:grid-cols-2 md:px-8 md:text-xs lg:mt-10'>
                <p className='max-w-[15rem]'>
                    {HERO.leftNote}
                </p>

                <p className='max-w-sm sm:justify-self-end sm:text-right'>
                    {HERO.rightNote}
                </p>
            </div>

            <a
                href='#about'
                className='absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 font-heading text-sm lg:flex'
            >
                scroll
                <PixelSprite rows={ICON_ARROW} className='h-2.5 w-auto rotate-90 animate-nudge' />
            </a>
        </section>
    )
}

export default HeroSection
